'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { CirclePlus, ChevronDown, ChevronRight, Clock, Lock, Sparkles, Settings, MessagesSquare, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { logger } from '@/lib/logger';
import { useSessionContext } from '@/contexts/session-context';
import { useSidebar } from '@/contexts/sidebar-context';
import { useLanguage } from '@/contexts/language-context';
import { useNotifications } from '@/contexts/notification-context';
import type { Notification } from '@/types/notification';
import SettingsModal from './settings-modal';
import { useAuth } from '@/contexts/auth-context';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { normalizeUserId } from '@/lib/user-session-rules';
import { SessionAccessDialog } from '@/components/session-access-dialog';
import { FeatureAccessDialog } from '@/components/feature-access-dialog';
import { getFeatureRestrictionFromResponse, normalizeUserLevel, type FeatureRestriction } from '@/lib/feature-permissions';
import { useLoginDialog } from '@/components/login-dialog';
import { getRecommendationAccessRestriction } from '@/lib/recommendation-access';
import { formatNotificationDate, getNotificationDate } from '@/lib/notification-date';
import { useNewChat } from '@/hooks/use-new-chat';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function Sidebar() {
  const { sessions, setSessions, setSessionStatus } = useSessionContext();
  const { isOpen, isMobile, closeSidebar } = useSidebar();
  const { t, isRTL, language } = useLanguage();
  const { notifications, setNotifications, setActiveNotification, markAsRead, unreadCount, loaded: notificationsLoaded } = useNotifications();
  const [hasMounted, setHasMounted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isPastChatsOpen, setIsPastChatsOpen] = useState(true);
  const [isInsightsOpen, setIsInsightsOpen] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSessionId, setDeleteSessionId] = useState<string | null>(null);
  const [recommendationDialog, setRecommendationDialog] = useState<{ open: boolean; restriction: FeatureRestriction }>({ open: false, restriction: 'login' });
  const router = useRouter();
  const pathname = usePathname();
  const { user, setUser, loading: authLoading, isLogin } = useAuth();
  const { openLoginDialog } = useLoginDialog();
  const userLevel = user?.level ?? 'L0';
  const accountLevel = normalizeUserLevel(user?.level);
  const newChat = useNewChat();
  const isSessionLocked = newChat.isLocked;

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Fetch sessions once on mount (frontend caching - no refetch on "New Chat")
  useEffect(() => {
    if (authLoading) return;

    logger.info('[Sidebar] Calling fetchSessions()...');
    if (user?.userId) {
      fetchSessions(user.userId);
      fetchNotifications();
      return;
    }

    setSessions([]);
    setSessionStatus('ready');
    setNotifications([]);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.userId]);

  function showRecommendationPrompt(restriction: FeatureRestriction) {
    setRecommendationDialog({ open: true, restriction });
  }

  function closeMobileSidebar() {
    if (isMobile) {
      closeSidebar();
    }
  }

  function getNotificationRestrictionTitle(restriction: FeatureRestriction | null): string | undefined {
    if (!restriction) return undefined;
    return restriction === 'login' ? 'Sign in to view insight' : 'Upgrade to view advanced insight';
  }

  async function fetchSessions(userId: string) {
    setLoading(true);
    setSessionStatus('loading');
    try {
      const res = await fetch(`/api/sessions?userId=${encodeURIComponent(String(userId))}`);

      if (!res.ok) {
        // Handle authentication errors by redirecting to login
        if (res.status === 401) {
          logger.warn('[Sidebar] Authentication failed - redirecting to login');
          // window.location.href = '/api/auth/login';
          const data = await res.json();
          toast.error(data.error, { position: 'top-center' });
          setUser(null);
          return;
        }
        throw new Error(`Failed to fetch sessions: ${res.status}`);
      }

      const data = await res.json();
      logger.info('[Sidebar] Fetched sessions from API:', data.sessions?.length || 0);
      setSessions(data.sessions || []);
      setSessionStatus('ready');
      logger.info('[Sidebar] Context updated with sessions');
    } catch (error) {
      logger.error('Failed to fetch sessions:', error);
      setSessions([]);
      setSessionStatus('error');
    } finally {
      setLoading(false);
    }
  }

  async function fetchNotifications() {
    try {
      const normalizedUserId = normalizeUserId(user?.userId);
      const query = normalizedUserId ? `?userId=${encodeURIComponent(String(normalizedUserId))}` : '';
      const res = await fetch(`/api/notifications${query}`);

      if (!res.ok) {
        if (res.status === 401) {
          // Session auth handled by fetchSessions redirect — skip silently
          return;
        }
        throw new Error(`Failed to fetch notifications: ${res.status}`);
      }

      const data = await res.json();
      logger.info('[Sidebar] Fetched notifications:', data.notifications?.length || 0);
      setNotifications(data.notifications || []);
    } catch (error) {
      logger.error('Failed to fetch notifications:', error);
      setNotifications([]);
    }
  }

  async function handleNotificationClick(notification: Notification) {
    if (authLoading) return;

    const recommendationRestriction = getNotificationRestriction(notification);

    if (recommendationRestriction) {
      showRecommendationPrompt(recommendationRestriction);
      return;
    }

    // If notification already has a session, navigate directly
    if (notification.session_id) {
      if (!notification.read) {
        markAsRead(notification.notification_id);
      }
      router.push(`/chat/${notification.session_id}`);
      closeMobileSidebar();
      return;
    }

    // First-open: fetch detail by date, then navigate
    try {
      const routeKey = getNotificationRouteKey(notification);
      const encodedDate = encodeURIComponent(routeKey);
      const normalizedUserId = normalizeUserId(user?.userId);
      const params = new URLSearchParams();
      if (normalizedUserId) {
        params.set('userId', normalizedUserId);
      }
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await fetch(`/api/notifications/${encodedDate}${query}`);

      if (!res.ok) {
        const restriction = await getFeatureRestrictionFromResponse(res);
        if (restriction) {
          showRecommendationPrompt(restriction);
          return;
        }

        throw new Error(`Failed to fetch notification detail: ${res.status}`);
      }

      const data = await res.json();
      markAsRead(notification.notification_id);

      // First-open: store content in context and navigate with notification param
      if (data.content) {
        setActiveNotification({
          notificationId: routeKey,
          content: data.content,
          title: data.title || notification.title,
          type: data.type || notification.type,
        });
      }

      router.push(`/?notification=${encodeURIComponent(routeKey)}`);
      closeMobileSidebar();
    } catch (error) {
      logger.error('Failed to handle notification click:', error);
      toast.error('Failed to load daily report', { position: 'top-center' });
    }
  }

  function handleNewChat() {
    newChat.startNewChat(closeMobileSidebar);
  }

  function handleAccountClick() {
    if (!isLogin) {
      openLoginDialog();
      closeMobileSidebar();
      return;
    }

    setIsSettingsOpen(true);
  }

  function handleSessionClick(sessionId: string) {
    router.push(`/chat/${sessionId}`);
    closeMobileSidebar();
  }

  // Extract current session ID from pathname
  const currentSessionId = pathname.match(/\/chat\/([^\/]+)/)?.[1];

  function getNotificationRestriction(notification: Notification): FeatureRestriction | null {
    return getRecommendationAccessRestriction({
      level: userLevel,
      isLoggedIn: isLogin,
      notificationDate: getNotificationDate(notification),
    });
  }

  function getNotificationRouteKey(notification: Notification): string {
    return getNotificationDate(notification) || notification.notification_id;
  }

  if (!hasMounted) {
    return null;
  }

  function startRenamingSession(sessionId: string) {
    if (isSessionLocked) return;

    const session = sessions.find((s) => s.session_id === sessionId);
    setRenamingSessionId(sessionId);
    setRenameValue(session?.session_title || '');
  }

  function cancelRenamingSession() {
    setRenamingSessionId(null);
    setRenameValue('');
  }

  async function commitRenamingSession() {
    if (isSessionLocked) return;

    if (!renamingSessionId) return;
    const nextTitle = renameValue.trim();
    if (!nextTitle) {
      cancelRenamingSession();
      return;
    }

    // Call backend to rename session
    const dto = {
      sessionId: renamingSessionId,
      userId: user?.userId,
      session_title: nextTitle,
    };

    try {
      // Send PATCH request to backend
      const renameResponse = await fetch('/api/sessions', {
        method: 'PATCH',
        body: JSON.stringify(dto),
      });

      if (!renameResponse.ok) {
        toast.error('Failed to rename session', { position: 'top-center' });
        return;
      } else {
        const reSessionInfo = await renameResponse.json();
        if (!reSessionInfo?.session_title) {
          throw new Error('V Agent API error');
        }
        setSessions(sessions.map((s) => (s.session_id === renamingSessionId ? { ...s, session_title: reSessionInfo.session_title } : s)));
        cancelRenamingSession();
        toast.success('Session renamed successfully', { position: 'top-center' });
      }
    } catch (error) {
      logger.error('Failed to rename session:', error);
      toast.error('Failed to rename session', { position: 'top-center' });
      return;
    }
  }

  function requestDeleteSession(sessionId: string) {
    if (isSessionLocked) return;

    setDeleteSessionId(sessionId);
    setDeleteDialogOpen(true);
  }

  function confirmDeleteSession() {
    if (isSessionLocked) return;

    if (!deleteSessionId) return;

    async function deleteSession(sessionId: string) {
      try {
        const dto = {
          session_id: sessionId,
          user_id: user?.userId,
        };
        const deleteSessionResponse = await fetch('/api/sessions', {
          method: 'POST',
          body: JSON.stringify(dto),
        });
        if (!deleteSessionResponse.ok) {
          toast.error('Failed to delete session', { position: 'top-center' });
          return;
        } else {
          toast.success('Session deleted successfully', { position: 'top-center' });
          setSessions(sessions.filter((s) => s.session_id !== sessionId));
          router.push(`/`);
        }
      } catch (error) {
        logger.error('Failed to delete session:', error);
        toast.error('Failed to delete session', { position: 'top-center' });
        setDeleteDialogOpen(false);
        setDeleteSessionId(null);
        return;
      } finally {
        setDeleteDialogOpen(false);
        setDeleteSessionId(null);
      }
    }

    deleteSession(deleteSessionId);
  }

  // Helper function to format timestamps
  function formatTimestamp(iso: string): string {
    const date = new Date(iso);
    const now = new Date();

    // Compare calendar dates (not 24-hour periods)
    const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diffMs = nowDay.getTime() - dateDay.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      // Show time for today
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }
    if (diffDays === 1) return t('common.yesterday');
    if (diffDays < 7) return t('common.daysAgo', { days: diffDays });
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? t('common.weekAgo') : t('common.weeksAgo', { weeks });
    }
    // For older sessions, show date
    return date.toLocaleDateString();
  }

  // Calculate position class based on RTL and open state.
  // The same transform-based drawer works with the mobile and desktop widths.
  const getPositionClass = () => {
    if (isRTL) {
      return `right-0 border-l ${isOpen ? 'translate-x-0' : 'translate-x-full'}`;
    }
    return `left-0 border-r ${isOpen ? 'translate-x-0' : '-translate-x-full'}`;
  };

  const cancelLabel = language === 'zh' ? '取消' : 'Cancel';
  const isNewChatDisabled = newChat.isDisabled;

  return (
    <>
      {isMobile && isOpen && (
        <button
          type="button"
          aria-label={t('common.hideSidebar')}
          className="fixed inset-0 z-40 bg-black/55 backdrop-blur-[1px] md:hidden"
          onClick={closeSidebar}
        />
      )}
      <aside
        style={{ top: 0, bottom: 0, paddingTop: 64 }}
        className={`fixed w-[calc(100vw-48px)] max-w-80 bg-[#231f20] border-white/[0.06] flex flex-col z-50 transition-transform duration-300 md:z-40 md:w-[280px] md:max-w-none ${getPositionClass()}`}>
        {/* Sticky Header */}
        <div className="px-4 py-2 flex-shrink-0">
          {/* New Chat Button */}
          <button
            onClick={handleNewChat}
            disabled={isNewChatDisabled}
            title={newChat.buttonTitle}
            className={`w-full px-3 py-2.5 h-[38px] rounded-[12px] bg-white/[0.04] border-t border-white/[0.06] transition-colors flex items-center gap-2.5 ${
              isNewChatDisabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-white/[0.08] cursor-pointer'
            }`}>
            {isSessionLocked ? (
              <Lock className={`w-5 h-5 flex-shrink-0 ${isNewChatDisabled ? 'text-white/40' : 'text-white'}`} />
            ) : (
              <CirclePlus className="w-5 h-5 text-white flex-shrink-0" />
            )}
            <span className={`text-sm font-medium ${isNewChatDisabled ? 'text-white/45' : 'text-white'}`}>{t('sidebar.newChat')}</span>
          </button>
        </div>

        {/* Insights For You Section */}
        <div className="flex-shrink-0">
          <div className="px-4">
            <button
              onClick={() => setIsInsightsOpen(!isInsightsOpen)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 h-[38px] rounded-[12px] text-sm font-medium text-white/60 bg-white/[0.04] hover:bg-white/[0.08] hover:text-white/80 transition-all cursor-pointer">
              <Sparkles className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 text-left">{t('sidebar.insightsForYou')}</span>
              {unreadCount > 0 && <span className="bg-[#e45728] text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{unreadCount}</span>}
              {isInsightsOpen ? (
                <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />
              ) : (
                <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />
              )}
            </button>
          </div>

          {isInsightsOpen && (
            <div
              aria-label={t('sidebar.insightsForYou')}
              className="sidebar-scrollbar px-4 pb-2 mt-1 max-h-50 overflow-y-auto">
              <div className="space-y-1">
                {!notificationsLoaded ? (
                  <div className="text-white/30 text-sm text-center py-4">{t('sidebar.loading')}</div>
                ) : notifications.length === 0 ? (
                  <div className="text-white/30 text-sm text-center  py-4">{t('sidebar.noNotifications')}</div>
                ) : (
                  <>
                    {notifications.slice(0, 7).map((notification) => {
                      const restriction = getNotificationRestriction(notification);

                      return (
                        <button
                          key={notification.notification_id}
                          onClick={() => handleNotificationClick(notification)}
                          aria-disabled={!!restriction}
                          title={getNotificationRestrictionTitle(restriction)}
                          className={`w-full text-left px-3 py-2.5 rounded-lg border-t border-transparent hover:bg-white/[0.08] transition-colors cursor-pointer ${
                            !notification.read ? 'bg-white/[0.03]' : ''
                          } ${restriction ? 'text-white/55' : ''}`}>
                          <div className="flex items-start gap-2.5">
                            {!notification.read && <span className="w-2 h-2 rounded-full bg-[#e45728] flex-shrink-0 mt-1.5" />}
                            {/* ml-[18px] = unread dot w-2 (8px) + gap-2.5 (10px) to align with unread items */}
                            <div className={`flex-1 min-w-0 ${notification.read ? 'ml-[18px]' : ''}`}>
                              <div className={`text-xs font-medium truncate ${notification.read ? 'text-[#ccc]' : 'text-white'}`}>{notification.title}</div>
                              <div className="text-[10px] text-[#666] truncate">{notification.preview}</div>
                              <div className="text-[10px] text-[#666]">{formatNotificationDate(notification, language)}</div>
                            </div>
                            {restriction && <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-white/35" />}
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Recent Chats Section */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Recent Chats Header - Sticky */}
          <div className="px-4 flex-shrink-0 mt-2">
            <button
              onClick={() => setIsPastChatsOpen(!isPastChatsOpen)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 h-[38px] rounded-[12px] text-sm font-medium text-white/60 bg-white/[0.04] hover:bg-white/[0.08] hover:text-white/80 transition-all cursor-pointer">
              <Clock className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 text-left">{t('sidebar.recentChats')}</span>
              {isPastChatsOpen ? (
                <ChevronDown className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />
              ) : (
                <ChevronRight className="w-4 h-4 flex-shrink-0 transition-transform duration-200" />
              )}
            </button>
          </div>

          {/* Scrollable Session History */}
          {isPastChatsOpen && (
            <div
              aria-label={t('sidebar.recentChats')}
              className="sidebar-scrollbar flex-1 overflow-y-auto px-4 pb-4 mt-1">
              <div className="space-y-1">
                {loading ? (
                  <div className="text-white/30 text-sm text-center py-4">{t('sidebar.loading')}</div>
                ) : sessions.length === 0 ? (
                  <div className="text-white/30 text-sm text-center py-4">{t('sidebar.noSessions')}</div>
                ) : (
                  sessions.map((session) => (
                    <div
                      key={session.session_id}
                      className="relative group">
                      {renamingSessionId === session.session_id ? (
                        <div className={`w-full text-left px-3 py-2.5 rounded-lg border border-white/[0.10] bg-white/[0.04] ${isRTL ? 'pl-10' : 'pr-10'}`}>
                          <div className="flex items-center gap-2.5">
                            <MessagesSquare className="w-5 h-5 shrink-0 text-white/40" />
                            <div className="flex-1 min-w-0">
                              <input
                                autoFocus
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    commitRenamingSession();
                                  }
                                  if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelRenamingSession();
                                  }
                                }}
                                onBlur={() => commitRenamingSession()}
                                className="w-full bg-transparent border border-white/[0.12] rounded-md px-2 py-1 text-xs font-medium text-[#ccc] outline-none focus:border-white/[0.20]"
                                placeholder={t('sidebar.newConversation')}
                              />
                              <div className="mt-1 text-[10px] text-[#666]">{formatTimestamp(session.created_at)}</div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onMouseDown={() => {
                            if (renamingSessionId && renamingSessionId !== session.session_id) {
                              cancelRenamingSession();
                            }
                          }}
                          onClick={() => handleSessionClick(session.session_id)}
                          className={`w-full text-left px-3 py-2.5 rounded-lg border-t border-transparent transition-colors hover:bg-white/[0.08] cursor-pointer ${
                            currentSessionId === session.session_id ? 'bg-white/[0.04]' : ''
                          } ${isRTL ? 'pl-10' : 'pr-10'}`}>
                          <div className="flex items-center gap-2.5">
                            <MessagesSquare className="w-5 h-5 shrink-0 text-white/40" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-[#ccc] truncate">{session.session_title || t('sidebar.newConversation')}</div>
                              <div className="text-[10px] text-[#666]">{formatTimestamp(session.created_at)}</div>
                            </div>
                          </div>
                        </button>
                      )}

                      <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-2' : 'right-2'}`}>
                        {isSessionLocked ? (
                          <button
                            type="button"
                            disabled
                            title={newChat.lockedTitle}
                            aria-label={newChat.lockedTitle}
                            className="w-8 h-8 rounded-[10px] opacity-0 group-hover:opacity-100 transition-opacity bg-white/0 flex items-center justify-center cursor-not-allowed">
                            <Lock className="w-4 h-4 text-white/35" />
                          </button>
                        ) : (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                aria-label="More"
                                onMouseDown={() => {
                                  if (renamingSessionId && renamingSessionId !== session.session_id) {
                                    cancelRenamingSession();
                                  }
                                }}
                                className="w-8 h-8 rounded-[10px] opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity bg-white/0 hover:bg-white/[0.08] flex items-center justify-center cursor-pointer">
                                <MoreHorizontal className="w-4 h-4 text-white/60" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              sideOffset={6}
                              align={isRTL ? 'start' : 'end'}
                              className="min-w-[140px] rounded-[12px] border border-white/[0.08] bg-[#231f20] text-white/80 shadow-lg">
                              <DropdownMenuItem
                                onSelect={() => startRenamingSession(session.session_id)}
                                className="cursor-pointer hover:bg-white/[0.06] focus:bg-white/[0.06] focus:text-white">
                                <Pencil className="w-4 h-4 text-white/50" />
                                <span>{t('sidebar.rename')}</span>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                variant="destructive"
                                onSelect={() => requestDeleteSession(session.session_id)}
                                className="cursor-pointer hover:bg-white/[0.06] focus:bg-white/[0.06] focus:text-[#ff8a80]">
                                <Trash2 className="w-4 h-4 text-[#ff8a80]" />
                                <span>{t('sidebar.delete')}</span>
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 px-4 p-4 border-t border-[rgba(255,255,255,0.06)]">
          <div className="w-full flex items-center gap-3 rounded-[12px]">
            <button
              type="button"
              onClick={handleAccountClick}
              aria-label={isLogin ? 'Open settings' : 'Log in'}
              className="min-w-0 flex flex-1 items-center gap-3 rounded-[12px] text-left cursor-pointer">
              <img
                className="w-9 h-9 rounded-full bg-[#F98715] text-white text-sm font-semibold flex items-center justify-center shrink-0"
                src={((user as unknown as { avatar?: string | null })?.avatar ?? '') || '/AI-logoImage.png'}
                alt="AICoach"
              />
              <div className="flex-1 min-w-0">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className={`min-w-0 truncate text-sm font-medium ${isLogin ? 'text-white' : 'text-[#F98715]'}`}>
                    {user?.email || t('sidebar.notLoggedIn')}
                  </span>
                  {isLogin && (
                    <span
                      data-testid="account-level-badge"
                      className="inline-flex h-4 min-w-[31px] shrink-0 items-center justify-center rounded-full border border-[#FA8614]/[0.44] bg-[#FA8714]/20 px-1 text-[9px] font-medium leading-none text-[#F98715]">
                      {accountLevel}
                    </span>
                  )}
                </div>
                <div className="text-xs text-white/40 truncate">Ask Before You Trade</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setIsSettingsOpen(true)}
              aria-label="Settings"
              className="group w-8 h-8 rounded-[10px] border border-white/0 flex items-center justify-center shrink-0 transition-colors hover:border-[#F98715]/15 hover:bg-[#e45728]/10 cursor-pointer">
              <Settings className="w-4 h-4 text-white/60 transition-colors group-hover:text-[#F98715]" />
            </button>
          </div>
        </div>
      </aside>
      {/* Settings Modal */}
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      <SessionAccessDialog
        open={newChat.limitDialog.open}
        onOpenChange={newChat.setLimitDialogOpen}
        variant="limit"
        language={language}
        level={userLevel}
        limit={newChat.limitDialog.limit}
      />

      <FeatureAccessDialog
        open={recommendationDialog.open}
        onOpenChange={(open) => setRecommendationDialog((prev) => ({ ...prev, open }))}
        restriction={recommendationDialog.restriction}
        feature="recommendation"
      />

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setDeleteSessionId(null);
        }}>
        <AlertDialogContent
          size="sm"
          className="bg-[#231f20] border border-white/[0.08]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{t('sidebar.delete')}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">{t('sidebar.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              variant="outline"
              className="border-white/[0.16] bg-white/[0.06] text-white hover:bg-white/[0.06] hover:text-white">
              {cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              className="bg-[#ff4d4f] text-white hover:bg-[#ff4d4f]/90 hover:text-white"
              onClick={confirmDeleteSession}>
              {t('sidebar.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
